import { Eye } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  MAX_REAPPLY_AFTER_MONTHS,
  REGRET_TIMINGS,
  REGRET_TIMING_HINTS,
  REGRET_TIMING_LABELS,
  isRegretSent,
  type RegretTiming,
  type FormFieldSettings,
  type JobDetail,
  type ScreeningQuestionInput,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Field } from '../../../components/Field';
import { Input } from '../../../components/Input';
import { Segmented } from '../../../components/Segmented';
import { useSaveJobStep } from '../api/jobs-api';
import { useStepAutosave } from '../hooks/use-step-autosave';
import { SectionMissing, sectionMissing } from './builder/SectionMissing';
import { FormBuilder, type QuestionDraft } from './FormBuilder';
import { FormPreview } from './FormPreview';
import type { StepProps } from './step-props';
import styles from './steps.module.css';
import { wholeNumber } from '../../../lib/whole-number';

/**
 * JOB-04 — step 3: the application form and its knockouts. The preview is the real
 * form definition the server builds — the one S13's apply form renders — not a
 * picture of it, so what the recruiter checks is what candidates get. It opens large,
 * in the middle of the screen (workflow review C7, ADR 0076).
 */

interface FormDraft {
  readonly formFields: FormFieldSettings;
  readonly questions: readonly QuestionDraft[];
  /** §L14 (DECISIONS-LOG S14 Q4). */
  readonly reapplyAfterMonths: number;
  /** Whether and when rejection emails go (DECISIONS-LOG S15 Q1; "Don't send" by default, ADR 0076). */
  readonly regretTiming: RegretTiming;
}

function draftOf(job: JobDetail): FormDraft {
  return {
    formFields: job.formFields,
    questions: job.questions.map((question) => ({ ...question })),
    reapplyAfterMonths: job.reapplyAfterMonths,
    regretTiming: job.regretTiming,
  };
}

export function FormStep({ job, isReadOnly, onConflict, footer }: StepProps): ReactNode {
  const [draft, setDraft] = useState<FormDraft>(() => draftOf(job));
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const saveStep = useSaveJobStep();
  const { state, schedule, flush } = useStepAutosave<FormDraft>({
    job,
    onConflict,
    save: (next, version) =>
      saveStep.mutateAsync({
        id: job.id,
        step: 'form',
        body: {
          version,
          formFields: next.formFields,
          questions: next.questions.map(forSaving),
          reapplyAfterMonths: next.reapplyAfterMonths,
          regretTiming: next.regretTiming,
        },
      }),
  });

  return (
    <div className={styles.step}>
      <Card
        title="Application form"
        meta="What candidates fill in when they apply"
        action={
          <>
            <SectionMissing missing={sectionMissing(job, 'form')} />
            <Button size="sm" variant="ghost" icon={Eye} onClick={() => void flush().then(() => setIsPreviewOpen(true))}>
              Preview
            </Button>
          </>
        }
      >
        <FormBuilder
          formFields={draft.formFields}
          questions={draft.questions}
          criteriaTypes={job.criteria.map((criterion) => criterion.type)}
          isReadOnly={isReadOnly}
          onChange={(form) => {
            const next = { ...draft, ...form };
            setDraft(next);
            schedule(next);
          }}
        />
      </Card>

      <Card title="Applying again" meta="How long someone waits before applying to this job again">
        <Field label="Months to wait" hint="0 lets them apply again straight away.">
          {({ inputId, describedBy }) => (
            <Input
              id={inputId}
              aria-describedby={describedBy}
              inputMode="numeric"
              isNumeric
              suffix="months"
              disabled={isReadOnly}
              value={String(draft.reapplyAfterMonths)}
              onChange={(event) => {
                const next = { ...draft, reapplyAfterMonths: Math.min(MAX_REAPPLY_AFTER_MONTHS, wholeNumber(event.target.value)) };
                setDraft(next);
                schedule(next);
              }}
            />
          )}
        </Field>
      </Card>

      <Card title="Rejection emails" meta="Whether people not selected get an email">
        <Segmented
          label="Send rejection emails"
          value={draft.regretTiming}
          disabled={isReadOnly}
          options={REGRET_TIMINGS.map((value) => ({ value, label: REGRET_TIMING_LABELS[value] }))}
          onChange={(regretTiming) => {
            const next = { ...draft, regretTiming };
            setDraft(next);
            schedule(next);
          }}
        />
        <p className={styles.hint}>
          {REGRET_TIMING_HINTS[draft.regretTiming]}
          {isRegretSent(draft.regretTiming) && ' It never gives the reason.'}
        </p>
      </Card>

      {footer({ state, flush })}

      {isPreviewOpen && <FormPreview job={job} onClose={() => setIsPreviewOpen(false)} />}
    </div>
  );
}

/** Blank option lines are what a half-typed list looks like; they are not sent. */
function forSaving(question: QuestionDraft): ScreeningQuestionInput & { id: string } {
  return {
    ...question,
    options: question.options ? question.options.map((option) => option.trim()).filter((option) => option !== '') : null,
  };
}
